import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import { useTheme } from '../context/ThemeContext';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import WeightProgressComponent from '../components/analytics/WeightProgressComponent';
import FoodLogProgressComponent from '../components/analytics/FoodLogProgressComponent';
import { exportAnalyticsToExcel } from '../utils/analyticsExcelExport';

function AnalyticsPage() {
  const { selectedClient } = useClient();
  const { isDarkMode } = useTheme();
  const [activeSubTab, setActiveSubTab] = useState('nutrition'); // 'nutrition', 'body'
  const [exportPreset, setExportPreset] = useState('1m');
  const [customFrom, setCustomFrom] = useState(() => {
    const t = new Date();
    t.setMonth(t.getMonth() - 1);
    return t.toISOString().split('T')[0];
  });
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [exportingType, setExportingType] = useState(null);

  const themeClasses = {
    bgPrimary: isDarkMode 
      ? ' bg-canvas  ' 
      : ' bg-forest-faint  ',
    bgCard: isDarkMode ? 'bg-surface/90' : 'bg-white/90',
    bgSecondary: isDarkMode ? 'bg-moss/50' : 'bg-moss',
    textPrimary: isDarkMode ? 'text-white' : 'text-ink',
    textSecondary: isDarkMode ? 'text-ink-muted' : 'text-ink-muted',
    textMuted: isDarkMode ? 'text-ink-muted' : 'text-ink-muted',
    borderPrimary: isDarkMode ? 'border-forest/20' : 'border-forest/30',
    btnPrimary: 'bg-forest-ondark hover:bg-forest-ondark/90 dark:text-canvas-dark',
    textGreen: 'text-forest-ondark dark:text-forest',
    textFat: 'text-amber-700 dark:text-amber-400',
    textRed: isDarkMode ? 'text-red-400' : 'text-red-600',
    textBlue: isDarkMode ? 'text-progress' : 'text-progress',
    shadowCard: isDarkMode ? ' ' : '',
  };

  // Language state
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // Translations
  const translations = {
    english: {
      'analytics.title': 'Analytics Dashboard',
      'analytics.subtitle': 'Track nutrition and body metrics over time',
      'analytics.selectClient': 'Please select a client to view analytics',
      'analytics.export.title': 'Export to Excel',
      'analytics.export.hint': 'Downloads weight/body measurements and detailed food logs for the selected period.',
      'analytics.export.period': 'Period',
      'analytics.export.from': 'From',
      'analytics.export.to': 'To',
      'analytics.export.button': 'Download Excel',
      'analytics.export.working': 'Preparing…',
      'analytics.export.body': 'Body Graph',
      'analytics.export.food': 'Food Logs',
      'analytics.export.err.missing': 'Please choose both start and end dates for a custom range.',
      'analytics.export.err.invalid': 'Invalid dates.',
      'analytics.export.err.order': 'Start date must be before end date.',
      'analytics.export.err.generic': 'Export failed. Please try again.',
    },
    hebrew: {
      'analytics.title': 'לוח בקרה אנליטי',
      'analytics.subtitle': 'עקוב אחר מדדי תזונה וגוף לאורך זמן',
      'analytics.selectClient': 'אנא בחר לקוח כדי לראות אנליטיקה',
      'analytics.export.title': 'ייצוא לאקסל',
      'analytics.export.hint': 'מוריד מדידות גוף/משקל ויומן מזון מפורט לפי התקופה שנבחרה.',
      'analytics.export.period': 'תקופה',
      'analytics.export.from': 'מתאריך',
      'analytics.export.to': 'עד תאריך',
      'analytics.export.button': 'הורדת קובץ אקסל',
      'analytics.export.working': 'מכין…',
      'analytics.export.body': 'גרף גוף',
      'analytics.export.food': 'יומן מזון',
      'analytics.export.err.missing': 'נא לבחור תאריך התחלה וסיום לטווח מותאם.',
      'analytics.export.err.invalid': 'תאריכים לא תקינים.',
      'analytics.export.err.order': 'תאריך ההתחלה חייב להיות לפני תאריך הסיום.',
      'analytics.export.err.generic': 'הייצוא נכשל. נסה שוב.',
    }
  };
  const t = (key) => translations[language][key] || key;

  const handleExportExcel = async (exportType) => {
    if (!selectedClient?.user_code) return;
    setExportingType(exportType);
    try {
      const result = await exportAnalyticsToExcel({
        userCode: selectedClient.user_code,
        clientName: selectedClient.name || selectedClient.full_name || '',
        preset: exportPreset,
        customStart: exportPreset === 'custom' ? customFrom : undefined,
        customEnd: exportPreset === 'custom' ? customTo : undefined,
        exportType,
        language,
      });
      if (!result.ok) {
        const msg =
          result.error === 'missing_dates'
            ? t('analytics.export.err.missing')
            : result.error === 'invalid_dates'
              ? t('analytics.export.err.invalid')
              : result.error === 'range_order'
                ? t('analytics.export.err.order')
                : t('analytics.export.err.generic');
        alert(msg);
      }
    } catch (e) {
      console.error(e);
      alert(t('analytics.export.err.generic'));
    } finally {
      setExportingType(null);
    }
  };

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  const exportPeriodButtons = [
    { key: '1m', labelEn: '1 month', labelHe: 'חודש' },
    { key: '3m', labelEn: '3 months', labelHe: '3 חודשים' },
    { key: '6m', labelEn: '6 months', labelHe: '6 חודשים' },
    { key: '1y', labelEn: '1 year', labelHe: 'שנה' },
    { key: 'custom', labelEn: 'Custom', labelHe: 'מותאם' },
  ];

  const excelExportSection = (
    <div
      data-tour="playbook-analytics-export"
      className={`mt-6 sm:mt-8 ${themeClasses.bgCard} rounded-lg p-4 sm:p-6 border  ${isDarkMode ? 'border-line' : 'border-line'} ${themeClasses.shadowCard}`}
    >
      <h2 className={`text-lg font-bold ${themeClasses.textPrimary} mb-1`}>
        {t('analytics.export.title')}
      </h2>
      <p className={`text-xs sm:text-sm ${themeClasses.textMuted} mb-4`}>
        {t('analytics.export.hint')}
      </p>
      <div className="flex flex-col gap-4">
        <div>
          <p className={`text-xs font-medium ${themeClasses.textSecondary} mb-2`}>
            {t('analytics.export.period')}
          </p>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {exportPeriodButtons.map((btn) => (
              <button
                key={btn.key}
                type="button"
                onClick={() => setExportPreset(btn.key)}
                disabled={Boolean(exportingType)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  exportPreset === btn.key
                    ? 'bg-forest-ondark text-white dark:text-canvas-dark  '
                    : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:opacity-90`
                }`}
              >
                {language === 'hebrew' ? btn.labelHe : btn.labelEn}
              </button>
            ))}
          </div>
        </div>
        {exportPreset === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 sm:gap-4">
            <label className="flex flex-col gap-1">
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t('analytics.export.from')}</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                disabled={Boolean(exportingType)}
                className={`rounded-lg px-3 py-2 text-sm border ${isDarkMode ? 'bg-moss border-line text-white' : 'bg-surface border-line text-ink'}`}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={`text-xs ${themeClasses.textSecondary}`}>{t('analytics.export.to')}</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                disabled={Boolean(exportingType)}
                className={`rounded-lg px-3 py-2 text-sm border ${isDarkMode ? 'bg-moss border-line text-white' : 'bg-surface border-line text-ink'}`}
              />
            </label>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleExportExcel('body')}
            disabled={Boolean(exportingType)}
            className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold text-white ${themeClasses.btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed `}
          >
            {exportingType === 'body' ? t('analytics.export.working') : t('analytics.export.body')}
          </button>
          <button
            type="button"
            onClick={() => handleExportExcel('food')}
            disabled={Boolean(exportingType)}
            className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold text-white ${themeClasses.btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed `}
          >
            {exportingType === 'food' ? t('analytics.export.working') : t('analytics.export.food')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <ClientWorkspaceLayout>
      <div 
        className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 pb-12`}
        dir={direction}
      >
      <main data-tour="playbook-analytics-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className={`text-2xl sm:text-3xl font-bold ${themeClasses.textPrimary} mb-1 sm:mb-2`}>
            {t('analytics.title')}
          </h1>
          <p className={`text-sm sm:text-base ${isDarkMode ? 'text-forest' : 'text-forest-ondark'}`}>
            {t('analytics.subtitle')}
          </p>
        </div>

        {/* Sub-tabs Navigation */}
        <div data-tour="playbook-analytics-tabs" className={`mb-6 flex gap-2 sm:gap-4 border-b ${isDarkMode ? 'border-line' : 'border-line'} overflow-x-auto flex-nowrap hide-scrollbar`}>
          <button
            data-tour="playbook-analytics-tab"
            onClick={() => setActiveSubTab('nutrition')}
            className={`whitespace-nowrap px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium transition-all ${
              activeSubTab === 'nutrition'
                ? `${themeClasses.textPrimary} border-b-2 ${isDarkMode ? 'border-forest/40' : 'border-forest/40'}`
                : `${themeClasses.textMuted} hover:${themeClasses.textPrimary}`
            }`}
          >
            {language === 'hebrew' ? 'אנליטיקת תזונה' : 'Nutrition Analytics'}
          </button>
          <button
            onClick={() => setActiveSubTab('body')}
            className={`whitespace-nowrap px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium transition-all ${
              activeSubTab === 'body'
                ? `${themeClasses.textPrimary} border-b-2 ${isDarkMode ? 'border-forest/40' : 'border-forest/40'}`
                : `${themeClasses.textMuted} hover:${themeClasses.textPrimary}`
            }`}
          >
            {language === 'hebrew' ? 'אנליטיקת גוף' : 'Body Analytics'}
          </button>
        </div>

        {/* Body Analytics Tab Content */}
        {activeSubTab === 'body' && (
          <>
            <WeightProgressComponent 
              userCode={selectedClient.user_code} 
              themeClasses={themeClasses} 
              language={language}
              isDarkMode={isDarkMode}
            />
            {excelExportSection}
          </>
        )}

        {/* Nutrition Analytics Tab Content */}
        {activeSubTab === 'nutrition' && (
          <div className="space-y-6 sm:space-y-8">
            <FoodLogProgressComponent 
              userCode={selectedClient.user_code} 
              themeClasses={themeClasses} 
              language={language}
              isDarkMode={isDarkMode}
              clientId={selectedClient.id}
            />
            {excelExportSection}
          </div>
        )}
      </main>
    </div>
  </ClientWorkspaceLayout>
  );
}

export default AnalyticsPage;